/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { NextRequest } from "next/server";

import { getBestLocaleFromRequest } from "./locale";

const EULA_REFERENCE_PATH = "eula-reference";
const LOCALE_SUFFIX_REGEX = /^-([a-zA-Z]{2}(-[a-zA-Z]{2})?)$/;

/**
 * Issues a 302 redirect to `pathname`, preserving the original query string and host.
 *
 * @param request - The incoming Next.js request whose `nextUrl` is mutated in-place.
 * @param pathname - Absolute pathname for the redirect target (e.g., `"/eula"`).
 * @returns A 302 `Response` with the `Location` header set.
 */
const redirect = (request: NextRequest, pathname: string): Response => {
	const url = request.nextUrl.clone();
	url.pathname = pathname;
	return Response.redirect(url);
};

/** Builds a localized EULA reference slug without adding a URL locale prefix. */
const buildEulaRefPath = (locale: string, eulaReferenceSlug: string): string =>
	`/${EULA_REFERENCE_PATH}/${eulaReferenceSlug}-${locale.toLowerCase()}`;

/** Redirects EULA references to the slug for the language resolved from the request. */
function resolveEulaRefRedirect(
	request: NextRequest,
	locale: string,
	defaultLocale: string,
	eulaReferenceSlug: string,
	restPath: string,
	defaultEulaPath: string,
): Response | void {
	if (!restPath.startsWith(eulaReferenceSlug)) return;

	const suffix = restPath.slice(eulaReferenceSlug.length);
	if (suffix !== "" && !LOCALE_SUFFIX_REGEX.test(suffix)) {
		return;
	}

	const refLocale = suffix ? suffix.slice(1) : null;

	if (locale === defaultLocale) {
		return redirect(request, defaultEulaPath);
	}

	if (refLocale && locale.toLowerCase() === refLocale.toLowerCase()) {
		return;
	}

	return redirect(request, buildEulaRefPath(locale, eulaReferenceSlug));
}

/**
 * Redirects EULA reference URLs (`/eula-reference/*`) for the language resolved from the request.
 * A default-locale visitor goes to the real EULA; other visitors go to their own reference slug.
 * The real EULA page (`eulaSlug`) is never redirected, for any locale: it is the binding version.
 * Resolves language from `?lang`, EPIC_LOCALE, Accept-Language, or the default.
 * Legacy locale-prefixed paths are left for processLocale to redirect first.
 */
export function processEulaReference(
	request: NextRequest,
	locales: string[],
	defaultLocale: string,
	eulaReferenceSlug: string,
	eulaSlug: string = "eula",
	unsupportedLocaleMapping?: Record<string, string>,
): Response | void {
	const { pathname } = request.nextUrl;
	const pathnameHasLocale = locales.some((loc) => pathname.startsWith(`/${loc}/`) || pathname === `/${loc}`);
	if (pathnameHasLocale) return; // processLocale will strip the prefix first; ignore until path is clean

	const segments = pathname.split("/");
	// Only reference paths redirect; eula pages serve directly for every locale
	if (segments[1] !== EULA_REFERENCE_PATH) return;

	const locale = getBestLocaleFromRequest(request, locales, defaultLocale, unsupportedLocaleMapping);
	const rest = segments.slice(2); // no locale segment — slug is at [1], rest starts at [2]

	return resolveEulaRefRedirect(request, locale, defaultLocale, eulaReferenceSlug, rest.join("/"), `/${eulaSlug}`);
}
